import React, { useContext, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useUser } from '../contexts/UserContext'
import { showsUpgradeOffer } from '../data/plansConfig'
import { isAdminUser } from '../utils/userProfileFields'
import { SidebarContext } from '../App'

import {
  Search,
  Settings,
  HelpCircle,
  Sparkles,
  Zap,
  ChevronLeft,
  ChevronRight,
  Layers,
  Wand2,
  X,
  Globe,
  Image,
  Factory,
  ClipboardList
} from 'lucide-react'

const Sidebar = ({ onPremiumClick, onAuthClick }) => {
  const location = useLocation()
  const { user, isPremium } = useUser()
  const showUpgrade = showsUpgradeOffer(user, isPremium)
  const { isSidebarCollapsed: isCollapsed, setIsSidebarCollapsed: setIsCollapsed, isMobileMenuOpen, setIsMobileMenuOpen } = useContext(SidebarContext)

  const navigation = [
    {
      name: 'Explore',
      href: '/explore',
      icon: Globe,
      description: 'Browse and remix community designs'
    },
    {
      name: 'Create',
      href: '/create',
      icon: Wand2,
      description: 'Choose a tier, then design'
    },
    {
      name: 'Design Studio',
      href: '/studio',
      icon: Layers,
      description: 'Studio editor: continue in-progress work'
    },
    {
      name: 'Search',
      href: '/search',
      icon: Search,
      description: 'Find existing clothing items'
    },
    {
      name: 'My Designs',
      href: '/my-designs',
      icon: Image,
      description: 'Saved designs: open, order, or delete'
    },
    {
      name: 'My Custom Requests',
      href: '/my-custom-requests',
      icon: ClipboardList,
      description: 'Bespoke quote requests and status'
    },
    ...(isAdminUser(user)
      ? [
          {
            name: 'Gelato queue',
            href: '/admin/gelato-orders',
            icon: Factory,
            description: 'Approve paid budget orders for Gelato'
          },
          {
            name: 'Custom quotes',
            href: '/admin/custom-quotes',
            icon: ClipboardList,
            description: 'Review bespoke requests and set quotes'
          }
        ]
      : [])
  ]

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)

  const handleUpgradeClick = () => {
    setIsMobileMenuOpen(false)
    if (!user) {
      onAuthClick?.('signup')
      return
    }
    onPremiumClick()
  }


  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  return (
    <>
      {/* Mobile Overlay - only shows on mobile when menu is open */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile Sidebar - slides in from left on mobile/tablet */}
      <div className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 glass-effect border-r border-white/20 transition-transform duration-300 ease-in-out pt-16 ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex-1 flex flex-col h-full overflow-y-auto">
          {/* Close Button */}
          <div className="flex justify-end p-3 border-b border-white/20">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="p-2 text-gray-500 hover:text-gold-light rounded-xl hover:bg-white/30 transition-all duration-200"
              title="Close menu"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-3">
            {navigation.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  replace={item.href === '/create' && location.pathname === '/design'}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`sidebar-item group ${
                    isActive(item.href)
                      ? 'active'
                      : 'text-gray-700 hover:text-gold-light'
                  }`}
                >
                  <Icon className="h-6 w-6 mr-3 transition-all duration-200 group-hover:scale-110 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center">
                      <span className="font-medium">{item.name}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 truncate">{item.description}</p>
                  </div>
                </Link>
              )
            })}
            <Link
              to="/settings"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`sidebar-item group ${
                isActive('/settings') ? 'active' : 'text-gray-700 hover:text-gold-light'
              }`}
            >
              <Settings className="h-6 w-6 mr-3 transition-all duration-200 group-hover:scale-110 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center">
                  <span className="font-medium">Settings</span>
                </div>
                <p className="text-xs text-gray-500 mt-1 truncate">Account, billing, preferences</p>
              </div>
            </Link>
          </nav>

          {/* Pro upgrade card - Mobile */}
          {showUpgrade && (
            <div className="p-4 border-t border-white/20">
              <div className="gradient-bg-premium rounded-2xl p-4 shadow-lg">
                <div className="flex items-center mb-3">
                  <Zap className="h-5 w-5 text-gold mr-2 flex-shrink-0" />
                  <h3 className="text-sm font-bold text-gray-900">Upgrade to Pro</h3>
                </div>
                <p className="text-xs text-gray-700 mb-4">
                  More generations every week.
                </p>
                <button 
                  onClick={handleUpgradeClick}
                  className="button-premium w-full text-xs"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          )}

          {/* Footer - Mobile */}
          <div className="p-4 border-t border-white/20">
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <Sparkles className="h-5 w-5 text-gold flex-shrink-0" />
              <span>Powered by Style AI</span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar - unchanged, only shows on lg+ screens */}
      <div className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:pt-16 lg:pb-0 glass-effect border-r border-white/20 transition-all duration-300 ease-in-out ${
        isCollapsed ? 'lg:w-16' : 'lg:w-64'
      }`}>
      <div className="flex-1 flex flex-col min-h-0">
        {/* Toggle Button */}
        <div className="flex justify-end p-3 border-b border-white/20">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-2 text-gray-500 hover:text-gold-light rounded-xl hover:bg-white/30 transition-all duration-200 shadow-sm hover:shadow-md"
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-3">
          {navigation.map((item) => {
            const Icon = item.icon
            return (
              <Link
                key={item.name}
                to={item.href}
                replace={item.href === '/create' && location.pathname === '/design'}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`sidebar-item group ${
                  isActive(item.href)
                    ? 'active'
                    : 'text-gray-700 hover:text-gold-light'
                } ${isCollapsed ? 'justify-center' : ''}`}
                title={isCollapsed ? item.name : undefined}
              >
                {isCollapsed ? (
                  <div className="flex justify-center">
                    <Icon className="h-7 w-7 text-gray-700 hover:text-gold-light transition-all duration-200 group-hover:scale-110" />
                  </div>
                ) : (
                  <>
                    <Icon className="h-6 w-6 mr-3 transition-all duration-200 group-hover:scale-110" />
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="font-medium">{item.name}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{item.description}</p>
                    </div>
                  </>
                )}
              </Link>
            )
          })}
          <Link
            to="/settings"
            className={`sidebar-item group ${
              isActive('/settings') ? 'active' : 'text-gray-700 hover:text-gold-light'
            } ${isCollapsed ? 'justify-center' : ''}`}
            title={isCollapsed ? 'Settings' : undefined}
          >
            {isCollapsed ? (
              <div className="flex justify-center">
                <Settings className="h-7 w-7 text-gray-700 hover:text-gold-light transition-all duration-200 group-hover:scale-110" />
              </div>
            ) : (
              <>
                <Settings className="h-6 w-6 mr-3 transition-all duration-200 group-hover:scale-110" />
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="font-medium">Settings</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Account, billing, preferences</p>
                </div>
              </>
            )}
          </Link>
        </nav>

        {/* Pro upgrade card */}
        {showUpgrade && (
          <div className="p-4 border-t border-white/20">
            {!isCollapsed ? (
              <div className="gradient-bg-premium rounded-2xl p-4 shadow-lg">
                <div className="flex items-center mb-3">
                  <Zap className="h-5 w-5 text-gold mr-2" />
                  <h3 className="text-sm font-bold text-gray-900">Upgrade to Pro</h3>
                </div>
                <p className="text-xs text-gray-700 mb-4">
                  More generations every week.
                </p>
                <button 
                  onClick={handleUpgradeClick}
                  className="button-premium w-full text-xs"
                >
                  Upgrade Now
                </button>
              </div>
            ) : (
              <div className="flex justify-center">
                <button
                  onClick={handleUpgradeClick}
                  className="p-2 text-gold hover:text-gold-light hover:bg-white/30 rounded-xl transition-all duration-200"
                  title="Upgrade to Pro: more generations every week"
                >
                  <Zap className="h-6 w-6" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Footer - Desktop */}
        <div className="p-4 border-t border-white/20">
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-2'} text-xs text-gray-500`}>
            <Sparkles className="h-5 w-5 text-gold" />
            {!isCollapsed && <span>Powered by Style AI</span>}
          </div>
        </div>
      </div>
      </div>
    </>
  )
}

export default Sidebar 